import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, MessageCircle } from "lucide-react";
import Seo, { serviceSchema, breadcrumbSchema } from "@/lib/seo";
import PageHero from "@/components/PageHero";
import { Reveal, StaggerGroup, fadeUp } from "@/components/motion/Reveal";
import Icon from "@/components/Icon";
import CTASection from "@/components/CTASection";
import { SERVICES, getServiceBySlug } from "@/data/services";
import { useSite } from "@/hooks/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { waLink } = useSite();
  const service = getServiceBySlug(slug);
  if (!service) return <Navigate to="/services" replace />;

  const blocks = service.sections || service.columns || [];
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <Seo
        title={service.title}
        description={service.excerpt}
        path={`/services/${service.slug}`}
        image={service.image}
        schema={serviceSchema(service)}
      />
      <PageHero
        eyebrow={service.heroTagline}
        title={service.title}
        description={service.intro}
        image={service.image}
        breadcrumb={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: service.shortTitle, path: `/services/${service.slug}` },
        ]}
      />

      {/* Scope blocks */}
      <section className="section-pad">
        <div className="container-sky">
          <div className="mb-12 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
              <Icon name={service.icon} className="h-6 w-6" />
            </span>
            <span className="tech-tag text-brand">Service Scope · {service.id}</span>
          </div>

          <div className="grid gap-7 lg:grid-cols-2">
            {blocks.map((block, i) => (
              <Reveal key={block.title} delay={i * 0.05}>
                <div className="h-full rounded-[1.6rem] border border-slate-100 bg-white p-8 shadow-[0_4px_24px_rgba(3,56,106,0.05)]">
                  <h2 className="font-heading text-xl font-bold text-navy sm:text-2xl">{block.title}</h2>
                  <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {block.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" strokeWidth={3} />
                        <span className="text-sm font-medium leading-snug text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Inline CTA */}
          <Reveal className="mt-12">
            <div className="flex flex-col items-start justify-between gap-6 rounded-[1.6rem] border border-brand/20 bg-surface p-8 sm:flex-row sm:items-center">
              <div>
                <h3 className="font-heading text-xl font-bold text-navy sm:text-2xl">{service.cta}</h3>
                <p className="mt-2 text-sm text-slate-600">Speak with SKY about your specific requirements.</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link to="/contact" className="btn-primary-sky">Request a Quote <ArrowUpRight className="h-4 w-4" /></Link>
                <a href={waLink(`Hi SKY, I would like to enquire about ${service.title}.`)} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                  <MessageCircle className="h-4 w-4" /> WhatsApp Us
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Other services */}
      <section className="section-pad bg-white">
        <div className="container-sky">
          <h2 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">Other Services</h2>
          <StaggerGroup className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((s) => (
              <motion.div key={s.slug} variants={fadeUp()}>
                <Link to={`/services/${s.slug}`} className="card-light group flex h-full flex-col p-7">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                    <Icon name={s.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-navy">{s.title}</h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{s.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
                    Learn More <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <CTASection />
    </>
  );
}
